<script lang="ts">
// Pure payload target shared with the unit tests (pattern: ToccoRegistrationSeminarReview).
// For a ToccoModuleRun the waiting list derives from participation counts; every
// other entity (registration on the base module / event itself, CARW-1703)
// derives it from the raw Tocco data paths. Missing values keep the historical
// NaN-comparison semantics (never on the waiting list).
export const buildModuleRegistrationTarget = (
  entity: {
    __typename?: string
    fieldExternalId?: string | null
    fieldParticipationMax?: number | null
    fieldRegistrationCount?: number | null
  },
  toccoData: { paths?: { registration?: { value?: string }; participation_max?: { value?: string } } },
) => {
  if (entity.__typename === 'ToccoModuleRun') {
    return {
      id: entity.fieldExternalId,
      waitingList:
        entity.fieldParticipationMax !== null &&
        entity.fieldParticipationMax !== undefined &&
        (entity.fieldRegistrationCount ?? 0) >= entity.fieldParticipationMax,
    }
  }
  return {
    id: entity.fieldExternalId,
    waitingList:
      parseInt(toccoData.paths?.registration?.value ?? '') >= parseInt(toccoData.paths?.participation_max?.value ?? ''),
  }
}
</script>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { useGtm } from '@gtm-support/vue-gtm'
import { format, parse } from 'date-fns'
import {
  TaxonomyTermToccoCountryFieldsFragment,
  TermsByNameDocument,
  TermsDocument,
  ToccoWebinarFieldsFragment,
  ToccoBaseModuleFieldsFragment,
  ToccoCourseFieldsFragment,
  ToccoELearningFieldsFragment,
  ToccoIntroModuleFieldsFragment,
  ToccoModuleRunFieldsFragment,
  TaxonomyTermToccoCooperationPartner,
  ConfigSettingsDocument,
} from '~/api/graphql/generated'
import type { TermsByNameQueryVariables, TermsQueryVariables } from '~/api/graphql/generated'
import { Option } from '~/components/atoms/forms/select/Select.vue'
import { FormKitMessages } from '@formkit/vue'
import { useRegistrationFormSubmit } from '~/api/drupal'
import { useTypedAsyncQuery } from '~/lib/utils/graphqlDocument'
import { formatToccoPrice } from '~/lib/utils/toccoUtils'
import { useI18n } from 'vue-i18n'

export interface AddressProps {
  street_address: string
  po_box: string
  postal_code: string
  city: string
  country: number
}

export interface AddressExtendedProps extends AddressProps {
  name: string
  hideCompany: boolean
}

export interface InvoiceAddressProps extends AddressProps {
  invoiceAddressActive: string
  department: string
  billing_information: string
}

export interface RegistrationModuleProps {
  personalInfo: {
    salutation: string
    firstname: string
    lastname: string
    email: string
    mobile: string
    phone: string
    position: string
    profession: string
    fphMemberNumber: string
    birthdate: string
  }
  privateAddress: AddressProps
  company: AddressExtendedProps
  invoiceAddress: InvoiceAddressProps
  message: string
}

const props = withDefaults(
  defineProps<{
    formData: RegistrationModuleProps
    addressRequired?: boolean
    entity:
      | ToccoBaseModuleFieldsFragment
      | ToccoCourseFieldsFragment
      | ToccoELearningFieldsFragment
      | ToccoIntroModuleFieldsFragment
      | ToccoModuleRunFieldsFragment
      | ToccoWebinarFieldsFragment
  }>(),
  {
    addressRequired: true,
  },
)

const emit = defineEmits(['cancel'])
const { languageCODE } = useLocale()
const { t } = useI18n()
const gtm = useGtm()
const { subscribe: subscribeNewsletter } = useCwbNewsletterSubscribe()

const isModuleRun = computed(() => props.entity.__typename === 'ToccoModuleRun')

const moduleRunBase = computed(() =>
  props.entity.__typename === 'ToccoModuleRun' ? props.entity.fieldBaseModule?.entity : null,
)

const fieldToccoDataJSON = computed(() => {
  const data = 'fieldToccoData' in props.entity ? props.entity.fieldToccoData : null
  if (!data) return {}
  if (typeof data === 'object') return data
  try {
    return JSON.parse(data) ?? {}
  } catch {
    return {}
  }
})

const reviewFormData = ref()

const termsVariables: TermsQueryVariables = {
  limit: 250,
  vocabulary: 'tocco_country',
  language: languageCODE.value,
}
const { data: termsData } = await useTypedAsyncQuery(TermsDocument, termsVariables)

const getCountryName = (externalId: number) => {
  const entities = termsData.value?.taxonomyTermQuery?.entities as TaxonomyTermToccoCountryFieldsFragment[]
  const foundEntity = entities.filter((entity: TaxonomyTermToccoCountryFieldsFragment) => {
    const fieldExternalId = entity?.fieldExternalId ?? 0
    return fieldExternalId === externalId
  })

  return foundEntity[0]?.name
}

const { data: configToccoSettings } = await useTypedAsyncQuery(ConfigSettingsDocument)

const pages = computed(() => {
  return {
    termsConditions: configToccoSettings.value?.configCareumToccoSettings?.termsAndConditionsPage ?? '/',
    privacyPolicy: configToccoSettings.value?.configCareumToccoSettings?.privacyPolicyPage ?? '/',
    thankYou: configToccoSettings.value?.configCareumToccoSettings?.moduleThankYouPage ?? '/',
  }
})

const legal = computed(() => {
  return {
    termsConditions: {
      label: t('tocco.registration.module.review.termsConditions.text', {
        hyperlink: `<a href="${pages.value.termsConditions}" target="_blank">
        ${t('tocco.registration.module.review.termsConditions.hyperlink')}
      </a>`,
      }),
      error: t('tocco.registration.module.review.termsConditions.error'),
    },

    privacyPolicy: {
      label: t('tocco.registration.module.review.privacyPolicy.text', {
        hyperlink: `<a href="${pages.value.privacyPolicy}" target="_blank">
        ${t('tocco.registration.module.review.privacyPolicy.hyperlink')}
      </a>`,
      }),
      error: t('tocco.registration.module.review.privacyPolicy.error'),
    },
  }
})

const showCooperationPartner = computed(() =>
  props.entity && props.entity.__typename == 'ToccoWebinar' ? false : true,
)

const formattedDate = computed(() =>
  props.entity && 'fieldStartDate' in props.entity && props.entity.fieldStartDate?.value
    ? format(parse(props.entity.fieldStartDate.value, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')
    : '',
)

const formattedBirthdate = computed(() =>
  props.formData.personalInfo.birthdate
    ? format(parse(props.formData.personalInfo.birthdate, 'yyyy-MM-dd', new Date()), 'dd.MM.yyyy')
    : '',
)
const formattedPrice = computed(() => {
  if (isModuleRun.value) {
    const price = moduleRunBase.value && 'fieldPrice' in moduleRunBase.value ? moduleRunBase.value.fieldPrice : null
    return formatToccoPrice(price)
  }
  return formatToccoPrice(fieldToccoDataJSON.value.paths?.price?.value)
})

const privateAddress = computed(() => {
  const address = props.formData.privateAddress
  if (
    !props.addressRequired &&
    address.country &&
    (address.city == null || address.city == '') &&
    (address.postal_code == null || address.postal_code == '') &&
    (address.street_address == null || address.street_address == '')
  ) {
    // check if this should be string
    address.country = 0
  }
  return address
})

const actualAddress: ComputedRef<AddressProps | InvoiceAddressProps> = computed(() =>
  props.formData.invoiceAddress.invoiceAddressActive === 'privateAddress'
    ? props.formData.privateAddress
    : props.formData.invoiceAddress.invoiceAddressActive === 'company'
      ? props.formData.company
      : props.formData.invoiceAddress,
)

const coopPartnersVariables: TermsByNameQueryVariables = {
  vocabulary: 'tocco_cooperation_partner',
  language: languageCODE.value,
  limit: 250,
}
const { data: coopPartnersData } = await useTypedAsyncQuery(TermsByNameDocument, coopPartnersVariables)

const cooperationPartnerOptions = reactive({
  entities: (coopPartnersData.value?.taxonomyTermQuery?.entities as TaxonomyTermToccoCooperationPartner[])?.map(
    (entity): Option => ({
      label: entity?.name ?? '',
      value: entity?.fieldExternalId ?? '',
    }),
  ),
})

const hasCooperationPartner = ref(false)

const payload = computed(() => ({
  personalInfo: props.formData.personalInfo,
  company: props.formData.company,
  invoiceAddress: props.formData.invoiceAddress,
  privateAddress: privateAddress.value,
  module: buildModuleRegistrationTarget(props.entity, fieldToccoDataJSON.value),
  cooperationPartnerId: hasCooperationPartner.value ? (reviewFormData.value?.cooperationPartner ?? null) : null,
  registrationTypeSeminar: reviewFormData.value?.consent,
  message: props.formData.message,
}))

const error = ref(false)
const isSubmitting = ref(false)

const cancel = () => {
  emit('cancel')
}

const handleSubmit = async () => {
  if (isSubmitting.value) return
  isSubmitting.value = true

  const clonedPayload = JSON.parse(JSON.stringify(payload.value))
  clonedPayload.personalInfo.phone = clonedPayload.personalInfo.phone.replace(/\s+/g, '')
  clonedPayload.personalInfo.mobile = clonedPayload.personalInfo.mobile.replace(/\s+/g, '')

  gtm?.trackEvent({
    event: 'Registration_CWB',
    cwb_type: 'module',
    cwb_title: props.entity.title,
  })

  let path = '/tocco/register/module'
  if (props.entity && props.entity.__typename == 'ToccoWebinar') {
    path = '/tocco/register/webinar'
  }

  try {
    await useRegistrationFormSubmit(path, JSON.stringify(clonedPayload))

    if (reviewFormData.value?.newsletter) {
      try {
        await subscribeNewsletter(clonedPayload.personalInfo)
      } catch (e) {
        console.log(e)
      }
    }

    navigateTo({ path: pages.value.thankYou })
  } catch {
    error.value = true
    isSubmitting.value = false

    if (import.meta.client) {
      setTimeout(() => window.scrollTo(0, 0), 40)
    }
  }
}
</script>

<template>
<FormKitLazyProvider config-file="true">

  <FormKit v-model="reviewFormData" type="form" :actions="false" @submit="handleSubmit">
    <div v-animate="{ animation: 'vertical-reveal' }" class="form-title">
      <h1>
        {{ t('tocco.registration.module.review.headings.title') }}
      </h1>
      <div v-if="error" class="form-error">
        <div class="icon">
          <Icon name="alert" />
        </div>
        <p>
          {{ t('tocco.registration.conference.review.error') }}
        </p>
      </div>
    </div>

    <div class="grid-columns-2">
      <div v-animate="{ animation: 'vertical-reveal' }" class="left">
        <h4>
          {{ t('tocco.registration.module.review.personalInfo.title') }}
        </h4>
        <div class="details-wrapper first">
          <p class="label">
            {{ t('tocco.registration.module.review.personalInfo.name') }}
          </p>
          <p class="label-information">
            <template v-if="String(formData.personalInfo.salutation) === '66'">
              {{ formData.personalInfo.firstname }} {{ formData.personalInfo.lastname }}
            </template>
            <template v-else>
              {{ t(`tocco.registration.personalInfo.salutationOptions.${formData.personalInfo.salutation}`) }}
              {{ formData.personalInfo.firstname }} {{ formData.personalInfo.lastname }}
            </template>
          </p>
        </div>
        <div class="details-wrapper">
          <p class="label">
            <span v-show="formData.personalInfo.mobile">
              {{ t('tocco.registration.module.review.personalInfo.mobile') }}
            </span>
            <span v-show="formData.personalInfo.phone">
              {{ t('tocco.registration.module.review.personalInfo.phone') }}
            </span>
          </p>
          <p class="label-information">
            <span v-show="formData.personalInfo.mobile">{{ formData.personalInfo.mobile }}</span>
            <span v-show="formData.personalInfo.phone">{{ formData.personalInfo.phone }}</span>
          </p>
        </div>
        <div class="details-wrapper">
          <p class="label">
            {{ t('tocco.registration.module.review.personalInfo.email') }}
          </p>
          <p class="label-information">
            {{ formData.personalInfo.email }}
          </p>
        </div>
        <div class="details-wrapper">
          <p class="label">
            {{ t('tocco.registration.module.review.personalInfo.address') }}
          </p>
          <p class="label-information">
            <span>{{ formData.privateAddress.street_address }}</span>
            <span>
              {{ formData.privateAddress.postal_code }}
              {{ formData.privateAddress.city }}
            </span>
            <span>
              {{ getCountryName(formData.privateAddress.country) }}
            </span>
          </p>
        </div>
        <div v-show="formData.personalInfo.position || formData.personalInfo.fphMemberNumber" class="details-wrapper">
          <p v-show="formData.personalInfo.position" class="label">
            {{ t('tocco.registration.module.review.personalInfo.position') }}
          </p>
          <p v-if="formData.personalInfo.position" class="label-information">
            {{ formData.personalInfo.position }}
          </p>
          <p v-show="formData.personalInfo.profession" class="label">
            {{ t('tocco.registration.module.review.personalInfo.profession') }}
          </p>
          <p v-if="formData.personalInfo.profession" class="label-information">
            {{ formData.personalInfo.profession }}
          </p>
          <div v-show="formData.personalInfo.fphMemberNumber" class="fph-number">
            <p class="label">
              {{ t('tocco.registration.module.review.personalInfo.fphMemberNumber') }}
            </p>
            <p class="label-information">
              {{ formData.personalInfo.fphMemberNumber }}
            </p>
          </div>
        </div>
        <div v-show="formData.personalInfo.birthdate" class="details-wrapper">
          <p class="label">
            {{ t('tocco.registration.module.review.personalInfo.birthdate') }}
          </p>
          <p class="label-information">
            {{ formattedBirthdate }}
          </p>
        </div>
        <div v-show="formData.message" class="details-wrapper">
          <p class="label">
            {{ t('tocco.registration.module.review.personalInfo.message') }}
          </p>
          <p class="label-information">
            {{ formData.message }}
          </p>
        </div>
      </div>

      <div v-animate="{ animation: 'vertical-reveal' }" class="right">
        <div v-show="!formData.company.hideCompany" class="top-right">
          <h4>
            {{ t('tocco.registration.module.review.company.title') }}
          </h4>
          <div class="details-wrapper first">
            <p class="label">
              {{ t('tocco.registration.module.review.company.name') }}
            </p>
            <p class="label-information">
              {{ formData.company.name }}
            </p>
          </div>
          <div class="details-wrapper">
            <p class="label">
              {{ t('tocco.registration.module.review.company.address') }}
            </p>
            <p class="label-information">
              <span>{{ formData.company.street_address }}</span>

              <span>{{ formData.company.postal_code }} {{ formData.company.city }}</span>

              <span> {{ getCountryName(formData.company.country) }}</span>
            </p>
          </div>
        </div>
        <div class="bottom-right">
          <h4>
            {{ t('tocco.registration.module.review.invoiceAddress.title') }}
          </h4>
          <div class="details-wrapper">
            <p class="label">
              {{ t('tocco.registration.module.review.invoiceAddress.address') }}
            </p>
            <p class="label-information">
              <span>{{ actualAddress.street_address }}</span>
              <span>
                {{ actualAddress.postal_code }}
                {{ actualAddress.city }}
              </span>
              <span> {{ getCountryName(actualAddress.country) }}</span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div v-animate="{ animation: 'vertical-reveal' }" class="summary-producs-wrapper">
      <div class="product-header">
        <h5 class="label">
          {{ t('tocco.registration.module.review.products.title') }}
        </h5>
      </div>
      <div class="product-review-item">
        <div class="product-review-item-left">
          <p v-if="entity" class="title">
            {{ entity.title }}
          </p>
          <div class="details">
            <p class="date-label">
              {{ t('tocco.registration.module.review.products.startDateLabel') }}
            </p>
            <p class="date">
              {{ formattedDate }}
            </p>
          </div>
        </div>
        <div class="product-review-item-right">
          <p class="price">
            {{ formattedPrice }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="showCooperationPartner" v-animate="{ animation: 'vertical-reveal' }" class="select-partner-wrapper">
      <div class="form-section">
        <Checkbox
          id="module-review-has-cooperation-partner"
          v-model="hasCooperationPartner"
          name="hasCooperationPartner"
          :label="t('tocco.registration.module.review.cooperationPartner')"
          :required="false"
        />
        <Select
          v-show="hasCooperationPartner"
          id="module-review-cooperation-partner"
          name="cooperationPartner"
          :options="cooperationPartnerOptions.entities"
          :label="t('tocco.registration.module.review.cooperationPartnerLabel')"
          validation="optional|min:1"
          :required="false"
        />
        <Checkbox
          id="module-review-consent"
          name="consent"
          :label="t('tocco.registration.module.review.consent')"
          :required="false"
        />
      </div>
    </div>

    <div v-animate="{ animation: 'vertical-reveal' }" class="form-section">
      <Checkbox
        id="module-review-terms-conditions"
        name="termsConditions"
        :label="legal.termsConditions.label"
        :required="true"
        :html-label="true"
        :validation-messages="{ accepted: legal.termsConditions.error }"
      />
      <Checkbox
        id="module-review-privacy-policy"
        name="privacyPolicy"
        :label="legal.privacyPolicy.label"
        :required="true"
        :html-label="true"
        :validation-messages="{ accepted: legal.privacyPolicy.error }"
      />
      <Checkbox
        id="module-registration-newsletter"
        name="newsletter"
        :label="t('tocco.registration.module.review.newsletter_consent')"
        :required="false"
      />
    </div>

    <div v-animate="{ animation: 'vertical-reveal' }" class="buttons-wrapper">
      <FormKitMessages />
      <Button secondary slide class="cancel-button" @click="cancel()">
        {{ t('tocco.registration.module.review.cancel') }}
      </Button>
      <Button primary slide class="submit-button">
        {{ t('tocco.registration.module.review.submit') }}
      </Button>
    </div>
  </FormKit>

</FormKitLazyProvider>
</template>

<style lang="scss" scoped>
@use '@/abstract/mixins' as *;
.form-title {
  width: 100%;
  margin-bottom: 72px;

  h6 {
    margin-bottom: 9px;
  }

  h1 {
    margin-bottom: 0;
  }

  .form-error {
    margin-top: 32px;
    color: var(--c-white);
    padding: 27px 29px;
    background-color: var(--c-red-90);

    display: flex;
    gap: 25px;
    align-items: center;

    icon {
      line-height: 1;
    }

    p {
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0.2px;

      margin-bottom: 0;
    }
  }

  @include breakpoint(tl) {
    margin-bottom: 96px;
  }
}

.grid-columns-2 {
  display: grid;
  padding-bottom: 65px;
  margin-bottom: 40px;
  width: 100%;

  border-bottom: 1px solid var(--c-line-light);

  grid-template-columns: 1fr;
  gap: 48px;

  @include breakpoint(tl) {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }

  h4 {
    margin-bottom: 18px;

    @include breakpoint(tl) {
      margin-bottom: 52px;
    }
  }

  .top-right {
    margin-bottom: 40px;
  }
}

.label {
  color: var(--c-primary);
  line-height: 30px;
}

.label-information {
  font-size: 16px;
  font-weight: 400;
}

.details-wrapper {
  display: grid;
  grid-template-columns: 130px 1fr;
  column-gap: 16px;
  align-items: baseline;

  border-bottom: 1px solid var(--c-line-light);
  padding: 16px 0;

  &.first {
    border-top: 1px solid var(--c-line-light);
  }

  p {
    margin-bottom: 0;
  }

  span {
    display: grid;
    line-height: 24px;
  }

  .fph-number {
    grid-column: 1 / span 2;
    display: grid;
    grid-template-columns: 130px 1fr;
    column-gap: 16px;
  }
}

.summary-producs-wrapper {
  width: 100%;

  .product-header {
    display: grid;
    grid-template-columns: 1fr 1fr;
    margin-top: 32px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--c-line-light);

    text-transform: uppercase;

    h5 {
      font-size: 13px;
    }

    .product-price-label {
      margin-left: auto;
      margin-right: 0;
    }

    @include breakpoint(tl) {
      margin-top: 24px;
      padding-bottom: 14px;

      .product-price-label {
        margin-right: 80px;
      }
    }
  }

  .product-review-item {
    display: grid;
    grid-template-columns: 1fr auto;
    border-bottom: 1px solid var(--c-line-light);
    padding: 18px 0;
    margin-bottom: 64px;
    column-gap: 24px;

    @include breakpoint(tl) {
      grid-template-columns: 1fr 210px;
    }

    &-left {
      .title {
        color: var(--c-text-dark);
        font-size: 20px;
        margin-bottom: 0;
        font-weight: 700;
      }

      .details {
        display: inline-flex;
        align-items: center;

        .date-label {
          margin-bottom: 0;
        }

        .date-label,
        .date {
          font-size: 16px;
          margin-right: 10px;
          margin-bottom: 0;
          color: var(--c-text-dark);
        }
      }
    }

    &-right {
      position: relative;
      margin-left: auto;
    }
  }
}

.select-partner-wrapper {
  border-bottom: 1px solid var(--c-line-light);
  padding-bottom: 25px;
  margin-bottom: 65px;

  p {
    font-size: 16px;
  }
}

.buttons-wrapper {
  margin-top: 72px;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 30px;

  @include breakpoint(tl) {
    margin-top: 80px;

    justify-content: flex-end;
    flex-direction: row;
  }

  .formkit-messages {
    margin-right: auto;

    display: flex;
    justify-content: center;
  }
}
</style>
